import { Location } from '@angular/common';
import { Component, effect, inject, OnInit, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { AlertController, ModalController } from '@ionic/angular/standalone';
import { NetworkService, ApiWebSocketService, AuthService, StatusBarService, ThemeService } from '@shared';
import { FcmBackendRegistrationService } from './services/fcm-backend-registration.service';
import { VendorPushService } from './services/vendor-push.service';
import { VendorLocalNotificationService } from './services/vendor-local-notification.service';
import { VendorSessionTeardownService } from './services/vendor-session-teardown.service';
import { VendorSideMenuService } from './services/vendor-side-menu.service';
import { environment } from '../environments/environment';
import { debounceTime, distinctUntilChanged, filter, takeUntil, pairwise, startWith } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';

/** Match shared OfflinePopupComponent: ignore spurious offline right after resume. */
const OFFLINE_ALERT_SUPPRESS_AFTER_RESUME_MS = 8000;

@Component({
  selector: 'vendor-root',
  standalone: true,
  imports: [IonicModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly vendorPush = inject(VendorPushService);
  private readonly vendorLocalNotifications = inject(VendorLocalNotificationService);
  private readonly fcmBackendRegistration = inject(FcmBackendRegistrationService);
  private readonly sessionTeardown = inject(VendorSessionTeardownService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly networkService = inject(NetworkService);
  private readonly alertController = inject(AlertController);
  private readonly modalController = inject(ModalController);
  private readonly location = inject(Location);
  private readonly sideMenu = inject(VendorSideMenuService);
  private readonly ngZone = inject(NgZone);
  private readonly apiWebSocketService = inject(ApiWebSocketService);
  private readonly statusBar = inject(StatusBarService);
  private readonly theme = inject(ThemeService);
  private appStateListenerHandle: PluginListenerHandle | null = null;
  private backButtonListener: PluginListenerHandle | null = null;
  private lastResumeAt = 0;
  private readonly visibilityChangeBound = this.onVisibilityChange.bind(this);
  readonly isOnline = this.networkService.isOnline;
  private readonly destroy$ = new Subject<void>();
  /** One-shot delayed warn if native FCM token never arrives after login. */
  private fcmLagWarnTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    void this.statusBar.configure({ isDark: this.theme.isDark() });
    effect(() => {
      const isDark = this.theme.isDark();
      void this.statusBar.updateForTheme(isDark);
    });

    console.log('[Vendor App] AppComponent constructor called');
    console.log('[Vendor App] Current URL:', typeof window !== 'undefined' ? window.location.href : 'N/A');

    // Log router navigation events
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        console.log('[Vendor App] Navigation ended:', event.url);
        console.log('[Vendor App] Navigation URL after redirects:', event.urlAfterRedirects);
      });
    
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event) => {
        console.log('[Vendor App] Navigation started:', event.url);
      });
    
    this.router.events
      .pipe(filter((event): event is NavigationCancel => event instanceof NavigationCancel))
      .subscribe((event) => {
        console.warn('[Vendor App] Navigation cancelled:', event.url, event.reason);
      });
    
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        console.error('[Vendor App] Navigation error:', event.url, event.error);
      });
  }
  
  ngAfterViewInit(): void {
    console.log('[Vendor App] ngAfterViewInit called');
    console.log('[Vendor App] Router outlet after view init:', document.querySelector('router-outlet') ? 'found' : 'not found');
    console.log('[Vendor App] Current route:', this.router.url);
    console.log('[Vendor App] Router state:', this.router.routerState);
  }

  async ngOnInit(): Promise<void> {
    console.log('[Vendor App] AppComponent ngOnInit called');
    console.log('[Vendor App] Router outlet should be rendering');
    console.log('[Vendor App] Current URL:', typeof window !== 'undefined' ? window.location.href : 'N/A');
    console.log('[Vendor App] Router outlet element:', document.querySelector('router-outlet') ? 'found' : 'not found');
    console.log('[Vendor App] ion-app element:', document.querySelector('ion-app') ? 'found' : 'not found');
    
    // Mark app as loaded
    const loadedMarker = document.getElementById('vendor-app-loaded');
    if (loadedMarker) {
      console.log('[Vendor App] App loaded marker found');
    }
    
    // Hide splash screen
    this.hideSplashScreen();
    
    try {
      this.vendorPush.setupNotificationNavigationHandlers();

      if (this.vendorPush.isNative) {
        void this.vendorLocalNotifications.ensureReady();
      }

      // Native: Request permission on startup (before login) so user sees prompt immediately.
      // Web: Initialize FCM with VAPID key; permission requested early (before login) for better UX.
      console.log('[Vendor App] Platform check - isNative:', this.vendorPush.isNative);
      if (this.vendorPush.isNative) {
        console.log('[Vendor App] Native platform: requesting push permission on startup...');
        await this.vendorPush.requestPermissionOnStartup();
      } else {
        console.log('[Vendor App] Web platform: initializing FCM and requesting permission...');
        console.log('[Vendor App] VAPID Key:', environment.fcmVapidKey ? 'Set' : 'Not set');
        this.vendorPush.initWeb(environment.fcmVapidKey || null);
        await this.vendorPush.initializeWeb();
        console.log('[Vendor App] Web FCM initialization completed');
        
        // Verify permission was requested
        if (typeof window !== 'undefined' && 'Notification' in window) {
          console.log('[Vendor App] Final notification permission status:', Notification.permission);
        }
      }

      // Register token with backend when user is authenticated (works for both web and native).
      this.vendorPush.getTokenWhenAuthenticated().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (token) => {
          if (token) {
            if (this.fcmLagWarnTimer) {
              clearTimeout(this.fcmLagWarnTimer);
              this.fcmLagWarnTimer = null;
            }
            console.log('[Vendor App] Push token obtained, registering with backend...');
            this.fcmBackendRegistration.registerToken(token);
          } else {
            const p = this.authService.profile();
            if (p?.id) {
              // Normal race: combineLatest emits before FCM listener fires. Retry immediately; warn only if still stuck.
              if (this.vendorPush.isNative) {
                void this.vendorPush.retryNativePushIfNeeded('logged-in-no-token-yet');
              }
              if (this.fcmLagWarnTimer) {
                clearTimeout(this.fcmLagWarnTimer);
              }
              if (this.vendorPush.isNative) {
                this.fcmLagWarnTimer = setTimeout(() => {
                  this.fcmLagWarnTimer = null;
                  const stillNo =
                    !this.vendorPush.getNativeTokenPeek() && !!this.authService.profile()?.id;
                  if (stillNo) {
                    console.warn(
                      '[Vendor App] FCM token still missing after ~8s while logged in — check notification permission, google-services.json (com.cartco.vendor), and logcat (FirebaseMessaging, VendorPush)',
                    );
                  }
                }, 8000);
              }
            } else {
              if (this.fcmLagWarnTimer) {
                clearTimeout(this.fcmLagWarnTimer);
                this.fcmLagWarnTimer = null;
              }
              console.log('[Vendor App] Not logged in yet — push backend registration runs after login when token exists');
            }
          }
        },
        error: (error) => {
          console.error('[Vendor App] Push token initialization error:', error);
        }
      });

      // Second chance: cold start can time out or run before permission; after login / profile restore, try again.
      toObservable(this.authService.profile)
        .pipe(
          debounceTime(300),
          distinctUntilChanged((a, b) => a?.id === b?.id),
          filter((p): p is NonNullable<typeof p> => !!p?.id),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          void this.sessionTeardown.markSessionActive('session-restored');
          if (this.vendorPush.isNative) {
            void this.vendorPush.retryNativePushIfNeeded('session-active');
          }
        });

      // 401 / forced logout: shared interceptor clears tokens without vendor teardown.
      toObservable(this.authService.profile)
        .pipe(
          startWith(this.authService.profile()),
          pairwise(),
          filter(([prev, curr]) => !!prev?.id && !curr?.id),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          void this.sessionTeardown.teardown('session-cleared');
        });
    } catch (error) {
      console.error('[Vendor App] Error in ngOnInit:', error);
    }

    // Show popup when network is lost (same as user app). Run in NgZone so alert
    // presents when detection comes from native (Capacitor/polling) callbacks.
    this.networkService.online$
      .pipe(
        startWith(true),
        pairwise(),
        filter(([prev, curr]) => prev && !curr),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.shouldShowOfflineAlert()) return;
        this.ngZone.run(() => void this.showOfflineAlert());
      });

    // Resume/visibility-triggered WebSocket reconnect (same as user app)
    document.addEventListener('visibilitychange', this.visibilityChangeBound);
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', (state: { isActive: boolean }) => {
        if (state.isActive) this.handleAppResume();
      }).then((handle) => {
        this.appStateListenerHandle = handle;
      });

      void App.addListener('backButton', ({ canGoBack }) => {
        void this.ngZone.run(() => this.handleHardwareBack(canGoBack));
      }).then((handle) => {
        this.backButtonListener = handle;
      });
    }

    console.log('[Vendor App] AppComponent ngOnInit completed');
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.handleAppResume();
    }
  }

  private handleAppResume(): void {
    this.lastResumeAt = Date.now();
    this.apiWebSocketService.resumeFromBackground();
    this.vendorPush.consumePendingDeepLink();
    if (this.vendorPush.isNative) {
      void this.vendorPush.retryNativePushIfNeeded('app-resume');
    }
  }

  private async handleHardwareBack(canGoBack: boolean): Promise<void> {
    const topModal = await this.modalController.getTop();
    if (topModal) {
      await topModal.dismiss();
      return;
    }

    const topAlert = await this.alertController.getTop();
    if (topAlert) {
      await topAlert.dismiss();
      return;
    }

    if (this.sideMenu.isOpen()) {
      this.sideMenu.setOpen(false);
      return;
    }

    const path = this.router.url.split('?')[0].replace(/\/$/, '');
    const isLiveOrdersPage = path === '/tabs/orders' || path === '/tabs';

    if (isLiveOrdersPage || !canGoBack) {
      await App.minimizeApp();
      return;
    }

    this.location.back();
  }

  ngOnDestroy(): void {
    if (this.fcmLagWarnTimer) {
      clearTimeout(this.fcmLagWarnTimer);
      this.fcmLagWarnTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('visibilitychange', this.visibilityChangeBound);
    if (this.appStateListenerHandle) {
      this.appStateListenerHandle.remove();
      this.appStateListenerHandle = null;
    }
    if (this.backButtonListener) {
      void this.backButtonListener.remove();
      this.backButtonListener = null;
    }
  }

  /** Avoid false offline when WebView backgrounds or right after resume (does not affect FCM). */
  private shouldShowOfflineAlert(): boolean {
    if (typeof document !== 'undefined' && document.hidden) return false;
    const since = this.lastResumeAt > 0 ? Date.now() - this.lastResumeAt : Infinity;
    if (since < OFFLINE_ALERT_SUPPRESS_AFTER_RESUME_MS) return false;
    return true;
  }

  private async showOfflineAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: '📶 No Internet Connection',
      message: "You're currently offline. Please turn on Wi‑Fi or mobile data to continue enjoying our services.",
      buttons: ['OK'],
      backdropDismiss: false,
    });
    await alert.present();
  }

  private hideSplashScreen(): void {
    console.log('[Vendor App] Attempting to hide splash screen...');
    // Hide splash screen after a short delay to ensure smooth transition
    setTimeout(() => {
      const splashScreen = document.getElementById('splash-screen');
      if (splashScreen) {
        console.log('[Vendor App] Splash screen found, hiding...');
        splashScreen.style.opacity = '0';
        splashScreen.style.transition = 'opacity 0.3s ease';
        setTimeout(() => {
          splashScreen.style.display = 'none';
          console.log('[Vendor App] Splash screen hidden');
        }, 300);
      } else {
        console.warn('[Vendor App] Splash screen element not found');
      }
    }, 100);
  }
}
